import { useGetProjectLazyQuery } from "@repo/server-api";
import { useNotifier } from "@repo/shared/common";
import { useCallback, useEffect, useState } from "react";

import {
	calculateTotalPrice,
	type Project,
	type ProjectPosition,
	type ProjectTeamMember,
} from "../types/ProjectTypes";

import type {
	Project as ServerProject,
	AdditionalContactPersonInformation as ServerProjectContactPersonInformation,
	AdditionalContactInformation as ServerProjectContactInformation,
	AdditionalAccountingInformation as ServerProjectAccountingInformation,
	AdditionalPositionInformation as ServerProjectPositionInformation,
	AdditionalTeamMemberInformation as ServerProjectTeamMemberInformation,
	AdditionalTemplateInformation as ServerProjectTemplateInformation,
	ProjectAdditionalInformation,
	SalaryInformation,
} from "@repo/server-api";

const infoGetter = <TInfo extends ProjectAdditionalInformation & { __typename?: string }>(
	project: ServerProject,
	typeInfo: string,
): TInfo | null => {
	const infos = project.additionalInformation.filter((info): info is TInfo => {
		return (info as unknown as TInfo)?.__typename === typeInfo;
	});

	return infos.length > 0 ? infos[0] : null;
};

export function mapProject(node: ServerProject): Project {
	const contactInfo = infoGetter<ServerProjectContactInformation>(
		node,
		"AdditionalContactInformation",
	);

	const contactPersonInfo = infoGetter<ServerProjectContactPersonInformation>(
		node,
		"AdditionalContactPersonInformation",
	);

	const accountingInformation = infoGetter<ServerProjectAccountingInformation>(
		node,
		"AdditionalAccountingInformation",
	);

	const templateInformation = infoGetter<ServerProjectTemplateInformation>(
		node,
		"AdditionalTemplateInformation",
	);

	const projectPositions = node.additionalInformation
		.filter((pos): pos is ServerProjectPositionInformation => {
			return (
				(pos as unknown as ServerProjectPositionInformation)?.__typename ===
				"AdditionalPositionInformation"
			);
		})
		.map(
			(pos): ProjectPosition => ({
				id: pos.id,
				service: {
					id: pos.saleableServiceId,
					name: pos.serviceName,
					price: pos.servicePrice,
				},
				hours: pos.hours ?? null,
				pricePerHour: pos.pricePerHour ?? null,
				price: pos.price ?? null,
				priceRate: pos.priceRate ?? null,
				description: pos.description,
				accountingProfile:
					pos.accountingProfile != null
						? {
								id: pos.accountingProfile.id,
								name: pos.accountingProfile.name,
							}
						: null,
				totalPrice: null,
			}),
		)
		.sort((a, b) => a.service?.name.localeCompare(b.service?.name || "") || 0);

	projectPositions.forEach((position) => {
		position.totalPrice = calculateTotalPrice(position);
	});

	return {
		id: node.id,
		name: node.name,
		number: node.number,
		status: node.status,
		projectType: node.projectType,
		startDate: node.range.from,
		endDate: node.range.to,
		isDeleted: node.isDeleted,
		parentProject: node?.parentProjectId
			? {
					id: node.parentProjectId,
					name: node.parentProjectName || "",
				}
			: null,

		TemplateInformation: {
			id: templateInformation?.id || null,
			isInvoiceWithTimesheet: templateInformation?.isInvoiceWithTimesheet || false,
			isDetailTimesheet: templateInformation?.isDetailTimesheet || false,
		},
		contactInformation: {
			id: contactInfo?.id || null,
			client: contactInfo?.client
				? {
						id: contactInfo.client.id,
						name: contactInfo.client.displayName,
					}
				: null,
			contractor: contactInfo?.contractor
				? {
						id: contactInfo.contractor.id,
						name: contactInfo.contractor.displayName,
					}
				: null,
			endCustomer: contactInfo?.endCustomer ?? null,
		},
		contactPersonInformation: {
			id: contactPersonInfo?.id || null,
			clientContactPerson: contactPersonInfo?.clientContactPerson
				? {
						id: contactPersonInfo.clientContactPerson.id,
						name: contactPersonInfo.clientContactPerson.displayName,
					}
				: null,
			contractorContactPerson: contactPersonInfo?.contractorContactPerson
				? {
						id: contactPersonInfo.contractorContactPerson.id,
						name: contactPersonInfo.contractorContactPerson.displayName,
					}
				: null,
			contractorProjectLeader: contactPersonInfo?.contractorProjectLeader
				? {
						id: contactPersonInfo.contractorProjectLeader.id,
						name: contactPersonInfo.contractorProjectLeader.displayName,
					}
				: null,
		},
		accountingInformation: {
			id: accountingInformation?.id || null,
			accountingProfile: accountingInformation?.accountingProfile
				? {
						id: accountingInformation.accountingProfile.id,
						name: accountingInformation.accountingProfile.name,
					}
				: null,
			clientContractNumber: accountingInformation?.clientContractNumber || null,
			clientContractDate: accountingInformation?.clientContratDate || null,
			clientOrderingNumber: accountingInformation?.clientOrderingNumber || null,
			clientOrderingDate: accountingInformation?.clientOrderingDate || null,
			clientProjectNumber: accountingInformation?.clientProjectNumber || null,
			supplierProjectNumber: accountingInformation?.supplierProjectNumber || null,
			provision: accountingInformation?.provision || null,
			provisionDescription: accountingInformation?.provisionDescription || null,
			timeTrackingResolution: accountingInformation?.timeTrackingResolution || 1,
		},
		budgetHours: node.budget.hours,
		hasBudgetHours: node.budget.hasTimeLimit,
		budgetPrice: node.budget.amount,
		hasBudgetPrice: node.budget.hasAmountLimit,

		positions: projectPositions,

		teamMembers: node.additionalInformation
			.filter((tm): tm is ServerProjectTeamMemberInformation => {
				return (
					(tm as unknown as ServerProjectTeamMemberInformation)?.__typename ===
					"AdditionalTeamMemberInformation"
				);
			})
			.map(
				(tm): ProjectTeamMember => ({
					id: tm.id,
					teamMember:
						tm.employee != null
							? {
									id: tm.employee.id,
									name: tm.employee.displayName,
									isFreelancer: tm.employee.isAFreelancer ?? false,
									pricePerHour:
										tm.employee.additionalInformation.find(
											(info): info is SalaryInformation =>
												(info as SalaryInformation)?.__typename === "SalaryInformation",
										)?.pricePerHour ?? null,
								}
							: null,
					contractor:
						tm.contractor_of_employee != null
							? {
									id: tm.contractor_of_employee.id,
									name: tm.contractor_of_employee.displayName,
								}
							: null,
					worksOnPositions: tm.worksOnPositions.map((pos) => ({
						positionId: pos.positionId,
						pricePerHour: pos.pricePerHour ?? null,
						hours: pos.hours ?? null,
					})),
				}),
			)
			.sort((a, b) => a.teamMember?.name.localeCompare(b.teamMember?.name || "") || 0),

		// LEGACY STUFF
		saleableServiceGroups: node.saleableServiceGroups || [],
		saleableServices: node.saleableServices || [],
		saleableUnits: node.saleableUnits || [],
	};
}

export function useProjectLoader(id: string | undefined) {
	const [project, setProject] = useState<Project | null>(null);
	const [loading, setLoading] = useState(false);

	const [fetchProject] = useGetProjectLazyQuery({
		fetchPolicy: "no-cache",
	});

	const { notifyErrors, notifyException } = useNotifier();

	const loadProject = useCallback(async () => {
		if (id == null) {
			setProject(null);
			return;
		}

		setLoading(true);

		try {
			const result = await fetchProject({ variables: { id } });

			if (result.error) {
				notifyException(result.errors);
				setProject(null);
				return;
			}

			const node = result.data?.node as unknown as ServerProject;
			if (node?.__typename !== "Project") {
				notifyErrors([{ message: "Project not found" }]);
				setProject(null);
				return;
			}

			const mappedProject = mapProject(node);
			console.log("Loaded project:", mappedProject);
			setProject(mappedProject);
		} catch (error) {
			notifyException(error);
			setProject(null);
		} finally {
			setLoading(false);
		}
	}, [id, fetchProject, notifyErrors, notifyException]);

	useEffect(() => {
		loadProject();
	}, [loadProject]);

	return { project, loading };
}
